import { useDeferredValue, useMemo } from "react";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "dates/DatesContext";
import { useModuleEnabled } from "redux/selectors/modules";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "utils/dashboardLinks";
import { useGetV4ProductReportByDate } from "../api/product/v4ProductReportByDate";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "components/core/basic/PageContainer";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { buildMetric } from "views/merchandise/metrics";
import { useAppSelector } from "redux/hooks";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "components/table/utils";
import { useGetV4ProductReportTotals } from "../api/product/v4ProductReportTotals";
import { useStandardEntityMetrics } from "../../../views/merchandise/tables/metricUtils";
import ProductReportTable from "./ProductReportTable";
import { useProductReportColumns } from "./useProductReportColumns";

const ProductReportBody = () => {
  const acosOrRoas = useAcosOrRoas();
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const { start, end, group } = useDates();

  const standardMetricIds = useStandardEntityMetrics("product");

  const columns = useProductReportColumns();

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "price",
    "units",
    "sales",
    "total_acos",
    "page_views",
    "ad_sales",
    "ad_spend",
    "acos",
    "conversion_rate",
    "total_conv_rate",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      // Hidden to support filtering
      tags: false,
      variations: false,
      category_id: false,
      sales_state: false,
      inventory_state: false,
      inventory_stock_state: false,
      has_ads: false,
      pricing_plan_status: false,
      ad_cnt: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["product_title"],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "productReport",
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const selectedIdsFilter =
    selectedIds.length ? [{ id: "product_id", value: { op: "anyof", value: selectedIds } }] : [];

  // Fetch product report data grouped by date to populate chart
  const byDateOptionsGetter = useGetV4ProductReportByDate();
  const productsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );
  const totalOptionsGetter = useGetV4ProductReportTotals();
  const productTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total: productTotalsReportQuery.data?.data,
    };
    return standardMetricIds.map((metricId) =>
      buildMetric(marketPlace, data, metricId, COMPARE_METRIC_LOOKUP[metricId]),
    );
  }, [productTotalsReportQuery.data?.data, standardMetricIds, marketPlace]);

  const timeLineData = useMemo(() => {
    return productsByDateReportQuery.data?.data.map((row) => ({ ...row, name: row.start_date })) ?? [];
  }, [productsByDateReportQuery.data?.data]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"productReport"}
        title={"Performance Overview"}
        data={{
          date: timeLineData,
          total: productTotalsReportQuery.data?.data,
        }}
        loading={
          productsByDateReportQuery.isFetching &&
          (!productsByDateReportQuery.data || productsByDateReportQuery.isPlaceholderData)
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          productTotalsReportQuery.isFetching &&
          (!productTotalsReportQuery.data || productTotalsReportQuery.isPlaceholderData)
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"category"}
      >
        <ProductReportTable state={state} initialState={initialState} setters={setters} columns={columns} />
      </MetricsCard>
    </PageContainer>
  );
};

export default function ProductReportV2() {
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: "Product Report",
      href: "/user/report/product",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <ProductReportBody />
    </DatesProvider>
  );
}
